import { Suspense, lazy, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import ClonModuleButton from '../../components/ClonModuleButton';

/** Mini-Header oben in einem einfachen Modul-Wrapper (Joerg 2026-05-29):
 *  rechts der „Als Tab uebernehmen"-Knopf, sonst nichts. */
function ClonHeader({ widgetSlug, defaultName, icon }: { widgetSlug: string; defaultName: string; icon?: string }) {
  return (
    <div className="border-b border-default bg-card/30">
      <div className="max-w-7xl mx-auto px-4 h-11 flex items-center gap-1">
        <div className="ml-auto">
          <ClonModuleButton widgetSlug={widgetSlug} defaultName={defaultName} icon={icon} />
        </div>
      </div>
    </div>
  );
}

function withClonHeader(slug: string, name: string, icon: string, child: ReactNode) {
  return (
    <div>
      <ClonHeader widgetSlug={slug} defaultName={name} icon={icon} />
      <Suspense fallback={<Fallback />}>{child}</Suspense>
    </div>
  );
}

/**
 * Wrapper-Komponente fuer Tabs der Uebersicht-Sicht.
 *
 * Bekommt eine Modul-Page-Komponente und rendert sie unter der Tab-Bar.
 * Die Modul-Pages nutzen relative API-Pfade (/api/modules/...), die unter
 * jeder Route funktionieren — kein Modul-Routing-Kontext noetig.
 *
 * Detail-Spruenge (Klick auf einen Listen-Eintrag) fuehren weiter zu
 * /m/<modul>/... — der User verlaesst dann die Tab-Bar. „Zurueck" im Dokument
 * geht im Verlauf zurueck und trifft damit die Uebersicht mit ihren Parametern
 * (Dokument-Navigation R2).
 *
 * Adress-Parameter (Dokument-Navigation 1.49, geprueft 2026-09-30): Die Wrapper
 * hier belegen keine eigenen Parameter; nur UebersichtAufgaben nutzt `tab` fuer
 * seine Unter-Reiter. Eingebettete Listen (ReportsPage, TasksPage, Plantafel,
 * Bibliothek, Material) schreiben ihren Listenzustand in dieselbe Adresse —
 * Module duerfen daher `tab` nicht als eigenen Listen-Parameter verwenden.
 */

const UebersichtAufgabenPage = lazy(() => import('./UebersichtAufgaben'));
const ReportsPage = lazy(() => import('bauberichteV2/pages/ReportsPage'));
const KundenStammPage = lazy(() => import('stammdaten/pages/KundenStammPage'));
const MitarbeiterStammPage = lazy(() => import('./AdressbuchMitarbeiterListe'));
const MaterialStammPage = lazy(() => import('stammdaten/pages/MaterialStammPage'));
const PlantafelBoardPage = lazy(() => import('plantafel/pages/BoardPage'));
const LibraryPage = lazy(() => import('bibliothek/pages/LibraryPage'));
const UebersichtZeiterfassungPage = lazy(() => import('./UebersichtZeiterfassung'));
const UebersichtAuswertungenPage = lazy(() => import('./UebersichtAuswertungen'));
const UebersichtVerwaltungPage = lazy(() => import('./UebersichtVerwaltung'));

function Fallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
}

export function UebersichtAufgaben() {
  return <Suspense fallback={<Fallback />}><UebersichtAufgabenPage /></Suspense>;
}

export function UebersichtBerichte() {
  return withClonHeader('berichte.embed', 'Meine Berichte', 'FileText', <ReportsPage />);
}

export function UebersichtKunden() {
  return <Suspense fallback={<Fallback />}><KundenStammPage /></Suspense>;
}

export function UebersichtMitarbeiter() {
  return <Suspense fallback={<Fallback />}><MitarbeiterStammPage /></Suspense>;
}

export function UebersichtMaterial() {
  return withClonHeader('material.embed', 'Mein Material', 'Package', <MaterialStammPage />);
}

interface StubProps {
  label: string;
  modulePath?: string;
  description?: string;
}

export function UebersichtStub({ label, modulePath, description }: StubProps) {
  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <h1 className="text-2xl font-semibold mb-1">{label}</h1>
      {description ? <p className="text-sm text-muted-foreground mb-6">{description}</p> : null}
      <div className="rounded-lg border border-dashed border-default p-8 text-center text-sm text-muted-foreground">
        {modulePath ? (
          <>
            Inhalt wird im naechsten Schritt verlinkt.
            <br />
            Zur vollen Modul-Sicht:{' '}
            <a href={modulePath} className="text-primary hover:underline">{modulePath}</a>
          </>
        ) : (
          'Inhalt kommt in Kuerze.'
        )}
      </div>
    </div>
  );
}

export function UebersichtPlantafel() {
  return withClonHeader('plantafel.embed', 'Meine Plantafel', 'Calendar', <PlantafelBoardPage />);
}

export function UebersichtBibliothek() {
  return withClonHeader('bibliothek.embed', 'Meine Bibliothek', 'Library', <LibraryPage />);
}

export function UebersichtZeiterfassung() {
  return <Suspense fallback={<Fallback />}><UebersichtZeiterfassungPage /></Suspense>;
}

export function UebersichtAuswertungen() {
  return <Suspense fallback={<Fallback />}><UebersichtAuswertungenPage /></Suspense>;
}

export function UebersichtVerwaltung() {
  return <Suspense fallback={<Fallback />}><UebersichtVerwaltungPage /></Suspense>;
}
